import { type ApiResponse } from "apisauce"

import { request } from "../../../services/api"
import {
  type AnalyticsResponse,
  type EntityType,
  type FacetOption,
  type FavoritesResponse,
  type GlobalMapListResponse,
  type LeadQuickView,
  type ListEntityTab,
  type ListSortField,
  type MapFeaturesResponse,
  type ProjectQuickView,
  type RecentActivityByEntityResponse
} from "../redux/global-map.types"

const BASE = "/global-map"

// The non-viewport, non-search fields both `getMapFeatures` and `getFacets`
// send — the backend's snake_case query param names for `GlobalMapFilters`
// (Task 12), built by `buildFilterParams` in `global-map.thunks.ts`.
export interface FilterQueryParams {
  salesperson?: string
  project_manager?: string
  team_member?: string
  subcontractor?: string
  // Two separate params, not one shared `status` (real, reported bug fix —
  // see `GlobalMapFilters.projectStatusIds`/`leadStatusIds`'s own comment):
  // `Lead.Status`/`Project.ProjectStatus` collide numerically, so one shared
  // param made "check Planning" also check "New".
  project_status?: string
  lead_status?: string
  type?: string
  region?: string
  active_period_start?: string
  active_period_end?: string
  // "Look Around a Zip Code" — always sent together or neither; entity-
  // agnostic (no Lead/Project collision risk, unlike project_status/
  // lead_status). See `GlobalMapFilters.nearZip`/`nearRadiusMi`'s own
  // comment.
  near_zip?: string
  near_radius_mi?: string
  // The "Appointment" filter's "Future Appointments" option — narrows to
  // records with at least one real (non-cancelled), future appointment.
  // Same backend field the KPI row's own Appointments tile already reads
  // (`GlobalMapFilters.hasUpcomingAppointment`), now also reachable as an
  // explicit filter pick.
  has_upcoming_appointment?: string
  // "History" — widens the result to also include archived records. See
  // `GlobalMapFilters.includeHistory`'s own comment for the full contract.
  include_history?: string
}

export interface MapFeaturesQueryParams extends FilterQueryParams {
  north?: number
  south?: number
  east?: number
  west?: number
  zoom: number
  search?: string
  // Comma-joined `EntityType[]` (e.g. `"project"`/`"lead"`/`"project,lead"`)
  // — the layer toggle, now a real server-side filter (2026-09-19 reversal
  // of "the layer toggle is client-side only"). Omitted only when
  // `fetchMapFeatures`'s own `condition` has already refused to fire the
  // request (an empty active-layers selection would 400: the backend's
  // `entities` field rejects an empty value rather than defaulting to
  // "both", which is the opposite of what zero active layers means).
  entities?: string
  // The List tab's "Show on Map" bulk action — when set, the backend
  // returns exactly these record(s) as individual pins, overriding
  // bbox/filters/entities entirely (see `_MAP_IDS_PARAM`'s own backend
  // description). Declared as an array here, same convention
  // `ExportQueryParams.ids` already established — the comma-join happens
  // at the request call site below, not in the caller's own params object.
  ids?: string[]
  // The Global Map quick view's "Nearby" tab (`QuickViewNearby`) — a
  // pre-resolved point (the record's own coordinate, already on hand from
  // its quick-view payload), sent directly rather than round-tripped
  // through `near_zip`'s geocoding. Deliberately NOT part of
  // `FilterQueryParams`: this is a one-off call independent of the main
  // map's committed `near_zip`/`near_radius_mi` filter state, never synced
  // to the URL or read by `buildFilterParams`. Must be given together with
  // `near_radius_mi` (inherited from `FilterQueryParams`) and never
  // alongside `near_zip` — see `MapFeaturesQuerySerializer.validate()`.
  near_latitude?: number
  near_longitude?: number
}

export const getMapFeatures = (
  params: MapFeaturesQueryParams
): Promise<ApiResponse<MapFeaturesResponse>> =>
  request.api.get(`${BASE}/features`, {
    ...params,
    ids: params.ids?.length ? params.ids.join(",") : undefined
  })

// Facets are filter-aware (counts reflect the currently active filters) but
// take no free-text search term — see the brief's ruling in
// `global-map.thunks.ts`'s `fetchFacets`.
//
// This is the backend's OWN field spelling — snake_case for the three
// multi-word facets (`project_manager`, `team_member`, `project_type`),
// confirmed by hitting the real local `/global-map/facets` endpoint directly
// during Task 24 manual QA — not the app's `GlobalMapFacets` camelCase shape.
// There is no blanket snake_case-to-camelCase transform anywhere in the
// apisauce client (`services/api.ts`), so `fetchFacets` in `global-map.thunks.ts`
// maps this wire shape into `GlobalMapFacets` itself; nothing upstream of that
// mapper may assume the two shapes already match.
export interface GlobalMapFacetsResponse {
  salesperson: FacetOption[]
  project_manager: FacetOption[]
  team_member: FacetOption[]
  subcontractor: FacetOption[]
  status: FacetOption[]
  project_type: FacetOption[]
  region: FacetOption[]
}

export const getFacets = (
  params: FilterQueryParams
): Promise<ApiResponse<GlobalMapFacetsResponse>> =>
  request.api.get(`${BASE}/facets`, params)

// The Analytics tab's one aggregate request — reuses `FilterQueryParams`
// verbatim (same shared filter bar as Map/List, per the Analytics spec's
// own "Shared Filters" requirement), plus `entities` (the Lead/Project
// layer toggle) and `search`, the same two params `/features` sends beyond
// the base filter set.
export interface AnalyticsQueryParams extends FilterQueryParams {
  entities?: string
  search?: string
}

export const getAnalytics = (
  params: AnalyticsQueryParams
): Promise<ApiResponse<AnalyticsResponse>> =>
  request.api.get(`${BASE}/analytics`, params)

// The Map tab's own "Recent Activity in View" card — same request shape as
// `getAnalytics` (shared filters/search/entities), but a light, dedicated
// response instead of the full 9-section Analytics aggregate, since the Map
// tab is this app's highest-traffic surface and only ever needs this one
// section.
export const getRecentActivity = (
  params: AnalyticsQueryParams
): Promise<ApiResponse<RecentActivityByEntityResponse>> =>
  request.api.get(`${BASE}/recent-activity`, params)

export const getProjectQuickView = (
  id: string
): Promise<ApiResponse<ProjectQuickView>> =>
  request.api.get(`${BASE}/locations/project/${id}`)

export const getLeadQuickView = (
  id: string
): Promise<ApiResponse<LeadQuickView>> =>
  request.api.get(`${BASE}/locations/lead/${id}`)

export interface ToggleFavoriteResponse {
  is_favorite: boolean
}

// The quick-view header's star — permanent until toggled again (never
// expires, matches `LeadFavorite`'s own backend semantics). No body: the
// id in the URL, scoped by entity type the same way the two quick-view GETs
// above are, is the whole request.
export const toggleFavorite = (
  entityType: EntityType,
  id: string
): Promise<ApiResponse<ToggleFavoriteResponse>> =>
  request.api.post(`${BASE}/locations/${entityType}/${id}/favorite`)

// The empty-selection panel's favorites list, and the source array for
// "show all favorites" on the map — same `MapFeature` row shape `/features`
// itself returns (`FavoritesResponse`'s own comment), so no second render
// path is needed for either surface.
export const getFavorites = (): Promise<ApiResponse<FavoritesResponse>> =>
  request.api.get(`${BASE}/favorites`)

// The unified search suggestion panel (`GlobalRecordSearchInput`) — reuses
// `ProjectQuickView`/`LeadQuickView` verbatim for the result rows (the
// backend's own `GlobalMapSearchView` reuses its quick-view serializers for
// exactly this reason: a selected suggestion needs no second detail fetch).
// Not viewport-bounded — no `north`/`south`/`east`/`west` in this param
// shape, unlike `MapFeaturesQueryParams` — per the search spec's own "not
// restricted to the current visible map bounds" requirement.
export interface GlobalMapSearchResponse {
  query_fingerprint: string
  projects: ProjectQuickView[]
  leads: LeadQuickView[]
  project_total: number
  lead_total: number
  total: number
  correlation_id: string
}

export interface GlobalMapSearchParams extends FilterQueryParams {
  search?: string
}

export const getSearchResults = (
  params: GlobalMapSearchParams
): Promise<ApiResponse<GlobalMapSearchResponse>> =>
  request.api.get(`${BASE}/search`, params)

// The List tab (`GET /global-map/list`) — page-number-paginated, sorted rows
// over the same shared filters as `/features`/`/facets`
// (`FilterQueryParams`), plus its own sort/pagination knobs. `entities` here
// is the List tab's own layer toggle (mirroring
// `MapFeaturesQueryParams.entities`, not reused from it directly since the
// two params sit on unrelated response types).
export interface ListQueryParams extends FilterQueryParams {
  entities: string
  list_entity: ListEntityTab
  sort: ListSortField
  direction: "asc" | "desc"
  // 1-based; always sent (2026-09-20 backend contract change from cursor to
  // page-number pagination — there is no "omit for page 1" convention the
  // way there was for `cursor?`).
  page: number
  // Must be one of 5/10/20/50 — enforced server-side, not by this type.
  page_size: number
  // Pre-existing gap fixed alongside Task 8 (`useListFeatures.ts`): the
  // free-text search term reaches this endpoint the same way it reaches
  // `/features` (`fetchListRows`'s own `buildFilterParams(filters, search)`
  // call already sent it on the wire), but this type never declared the
  // field — invisible until a caller tried to read it back off a typed
  // `ListQueryParams` value, which the real request never needed to.
  search?: string
}

export const getListRows = (
  params: ListQueryParams
): Promise<ApiResponse<GlobalMapListResponse>> =>
  request.api.get(`${BASE}/list`, params)

// The List tab's Export button (Task 13) — same shared filters/search/
// entities/list_entity/sort as `/list` (no `page`/`page_size`: an export is
// the whole filtered result set, not one page of it), plus an optional
// `ids` — when the caller has a non-empty row selection, selection wins over
// "export everything the current filters match" (the spec's own stated
// precedence). `ids` is declared as an array here (not the comma-joined
// string the wire actually wants) so a caller building this thunk's params
// hands over the selection verbatim; the join happens at the very last step,
// right where the request goes out.
export interface ExportQueryParams extends FilterQueryParams {
  entities: string
  list_entity: ListEntityTab
  sort: ListSortField
  direction: "asc" | "desc"
  search?: string
  ids?: string[]
}

export const exportListRows = (
  params: ExportQueryParams
): Promise<ApiResponse<Blob>> =>
  request.api.get(
    `${BASE}/export/`,
    { ...params, ids: params.ids?.length ? params.ids.join(",") : undefined },
    { responseType: "blob" }
  )
